//#region header */
/**************************************************************************************************
//
//  Description: Form to display the index connection information
//
//  Copyright:    © 2026 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   05.01.26 Sean Flook         GMSCM-12 Initial Revision.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

import React, { useEffect, useState, useRef } from "react";

import useConfigQueryStore from "../context/ConfigStore";
import useUserQueryStore from "../context/UserStore";

import { IndexConnection, MultiTenantConfigOrganisation } from "../dtos/multiTenantConfigDto";
import ConfigErrorType from "../models/configErrorType";

import { Box, Button, Grid2, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ADSTextControl from "../components/ADSTextControl";
import ADSErrorDisplay from "../components/ADSErrorDisplay";

import { adsRed, adsRed10 } from "../utils/ADSColour";
import { dataFormStyle, FormRowStyle } from "../utils/ADSStyles";

interface IndexConnectionFormProps {
  parentTreeId: string;
  data: IndexConnection;
  errors: ConfigErrorType[] | undefined;
  onDataChange: (data: IndexConnection) => void;
  onAddElasticNode?: () => void;
}

const IndexConnectionForm: React.FC<IndexConnectionFormProps> = ({
  parentTreeId,
  data,
  errors,
  onDataChange,
  onAddElasticNode,
}) => {
  const currentUser = useUserQueryStore((s) => s.userQuery.currentUser);

  const revertChanges = useConfigQueryStore((s) => s.configQuery.revertChanges);
  const originalDocument = useConfigQueryStore((s) => s.configQuery.originalDocument);
  const organisationSelected = useConfigQueryStore((s) => s.configQuery.organisationSelected);

  const [alias, setAlias] = useState<string>("");
  const [indexUsername, setIndexUsername] = useState<string>("");
  const [indexPassword, setIndexPassword] = useState<string>("");

  const [aliasError, setAliasError] = useState<string[] | undefined>(undefined);
  const [usernameError, setUsernameError] = useState<string[] | undefined>(undefined);
  const [passwordError, setPasswordError] = useState<string[] | undefined>(undefined);
  const [nodesError, setNodesError] = useState<string[] | undefined>(undefined);

  const currentAlias = useRef<string>("");
  const currentUsername = useRef<string>("");
  const currentPassword = useRef<string>("");

  /**
   * Method to update the data when a field is changed.
   *
   * @param fieldName The name of the field that has been updated.
   * @param value The new value for the field.
   */
  const updateData = (fieldName: string, value: string | number | undefined): void => {
    let updatedIndexConnection: IndexConnection;
    updatedIndexConnection = {
      ...data,
      [fieldName]: value,
    };

    if (onDataChange) {
      onDataChange(updatedIndexConnection);
    }
  };

  /**
   * Event to handle when the alias is changed.
   *
   * @param newValue The new alias.
   */
  const handleAliasChangeEvent = (newValue: string): void => {
    setAlias(newValue ? newValue : "");
    currentAlias.current = newValue ? newValue : "";
    updateData("alias", newValue ? newValue : "");
  };

  /**
   * Event to handle when the username is changed.
   *
   * @param newValue The new username.
   */
  const handleUsernameChangeEvent = (newValue: string): void => {
    if (currentUser && newValue !== currentUser.auditName) {
      setIndexUsername(newValue ? newValue : "");
      currentUsername.current = newValue ? newValue : "";
      updateData("username", newValue ? newValue : "");
    }
  };

  /**
   * Event to handle when the password is changed.
   *
   * @param newValue Th new password.
   */
  const handlePasswordChangeEvent = (newValue: string): void => {
    if (currentUser && indexUsername && indexUsername !== currentUser.auditName) {
      setIndexPassword(newValue ? newValue : "");
      currentPassword.current = newValue ? newValue : "";
      updateData("password", newValue ? newValue : "");
    }
  };

  useEffect(() => {
    if (data) {
      setAlias(data.alias ? data.alias : "");
      setIndexUsername(data.username ? data.username : "");
      setIndexPassword(data.password ? data.password : "");
    }
  }, [data]);

  useEffect(() => {
    setAliasError(undefined);
    setUsernameError(undefined);
    setPasswordError(undefined);
    setNodesError(undefined);

    if (errors && errors.length > 0) {
      for (const error of errors) {
        if (error.treeId === parentTreeId || error.id === parentTreeId) {
          switch (error.field) {
            case "alias":
            case "elasticAlias":
              setAliasError(error.errors);
              break;

            case "username":
            case "elasticUsername":
              setUsernameError(error.errors);
              break;

            case "password":
            case "elasticPassword":
              setPasswordError(error.errors);
              break;

            case "elasticElasticNodes":
            case "elasticSettings":
              setNodesError(error.errors);
              break;

            default:
              break;
          }
        }
      }
    }
  }, [errors, parentTreeId]);

  useEffect(() => {
    if (revertChanges && originalDocument) {
      const originalOrganisation: MultiTenantConfigOrganisation = originalDocument.organisations.find(
        (x) => x.organisationName === organisationSelected,
      ) as MultiTenantConfigOrganisation;

      if (originalOrganisation) {
        // setAlias(
        //   variant === "search"
        //     ? originalOrganisation.namedElasticSettings.SearchIndexConnection.alias
        //       ? originalOrganisation.namedElasticSettings.SearchIndexConnection.alias
        //       : ""
        //     : originalOrganisation.namedElasticSettings.XDMIndexConnection.alias
        //     ? originalOrganisation.namedElasticSettings.XDMIndexConnection.alias
        //     : ""
        // );
        // setIndexUsername(
        //   variant === "search"
        //     ? originalOrganisation.namedElasticSettings.SearchIndexConnection.username
        //       ? originalOrganisation.namedElasticSettings.SearchIndexConnection.username
        //       : ""
        //     : originalOrganisation.namedElasticSettings.XDMIndexConnection.username
        //     ? originalOrganisation.namedElasticSettings.XDMIndexConnection.username
        //     : ""
        // );
        // setIndexPassword(
        //   variant === "search"
        //     ? originalOrganisation.namedElasticSettings.SearchIndexConnection.password
        //       ? originalOrganisation.namedElasticSettings.SearchIndexConnection.password
        //       : ""
        //     : originalOrganisation.namedElasticSettings.XDMIndexConnection.password
        //     ? originalOrganisation.namedElasticSettings.XDMIndexConnection.password
        //     : ""
        // );
      }
    }
  }, [revertChanges, originalDocument, organisationSelected]);

  return (
    <Box sx={dataFormStyle("OrganisationForm")}>
      <Grid2 container justifyContent="flex-start" alignItems="baseline" sx={FormRowStyle()}>
        <Grid2 size={12}>
          <ADSTextControl
            label="Alias"
            value={alias}
            maxLength={100}
            id="alias"
            isEditable={currentUser?.isSuperAdministrator}
            isRequired
            helperText="The name of the index alias."
            errorText={aliasError}
            onChange={handleAliasChangeEvent}
          />
          <ADSTextControl
            label="User"
            value={indexUsername}
            maxLength={100}
            id="indexUsername"
            isEditable={currentUser?.isSuperAdministrator}
            helperText="The id of the user to use to connect to the index."
            errorText={usernameError}
            onChange={handleUsernameChangeEvent}
          />
          <ADSTextControl
            label="Password"
            value={indexPassword}
            isHidden
            maxLength={100}
            id="indexPassword"
            isEditable={currentUser?.isSuperAdministrator}
            isRequired={!!indexUsername}
            helperText="The password for the user used to connect to the index."
            errorText={passwordError}
            onChange={handlePasswordChangeEvent}
          />
          <Box sx={{ mt: 2, p: 1.5, backgroundColor: nodesError ? adsRed10 : "transparent" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="subtitle2">
                Elastic nodes ({(data.elasticNodes ?? []).length})
              </Typography>
              {currentUser?.isSuperAdministrator && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => onAddElasticNode?.()}
                  sx={{ textTransform: "none" }}
                >
                  Add elastic node
                </Button>
              )}
            </Stack>
            {(data.elasticNodes ?? []).length === 0 ? (
              <Typography variant="caption" sx={{ color: nodesError ? adsRed : "text.secondary" }}>
                No elastic nodes yet. Add one here, or hover the elastic setting in the tree and click +.
              </Typography>
            ) : (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {(data.elasticNodes ?? []).map((node) => (
                  <Typography key={node} variant="caption">
                    {node}
                  </Typography>
                ))}
              </Stack>
            )}
            {nodesError && <ADSErrorDisplay id="elastic-nodes-error" errorText={nodesError} removeLabelSpacer />}
          </Box>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default IndexConnectionForm;
